import { useEffect, useState } from 'react';
import { messagingApi } from '../api/endpoints';
import { useAuth } from '../auth/AuthContext';

const INTERVAL_MS = 60_000;
const CHANGED_EVENT = 'barso:unread-changed';

/** Nach dem Lesen oder Schreiben aufrufen: der Zähler in der Navigation fragt sofort neu ab. */
export function notifyUnreadChanged(): void {
  window.dispatchEvent(new Event(CHANGED_EVENT));
}

/** Auf `notifyUnreadChanged` hören (das Postfach lädt seine Liste neu). Gibt die Abmeldung zurück. */
export function onUnreadChanged(listener: () => void): () => void {
  window.addEventListener(CHANGED_EVENT, listener);
  return () => {
    window.removeEventListener(CHANGED_EVENT, listener);
  };
}

/**
 * Anzahl ungelesener Nachrichten der angemeldeten Person: eigene Gespräche, Rundschreiben der Organisation und
 * Team-Gespräche, auf die eine Antwort wartet (Plan Nr. 40, Plan 16 / 4.13). Fragt alle 60 Sekunden ab, solange der Tab
 * sichtbar ist (kein WebSocket). Fehler bleiben still: Ein fehlender Zähler ist kein Grund für eine Meldung.
 */
export function useUnreadMessages(): number {
  const { status } = useAuth();
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (status !== 'authenticated') return;
    const guard = { cancelled: false };
    const load = (): void => {
      if (document.visibilityState !== 'visible') return;
      messagingApi.myUnread().then(
        (result) => {
          if (!guard.cancelled) setCount(result.threads + result.team);
        },
        () => undefined,
      );
    };
    load();
    const timer = window.setInterval(load, INTERVAL_MS);
    document.addEventListener('visibilitychange', load);
    window.addEventListener(CHANGED_EVENT, load);
    return () => {
      guard.cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', load);
      window.removeEventListener(CHANGED_EVENT, load);
    };
  }, [status]);

  // Nach der Abmeldung zählt der alte Stand nicht mehr
  return status === 'authenticated' ? count : 0;
}
